// @ts-nocheck
/**
 * Call Quality — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + bordered filter Card + CallQualityMetricsPanel). Byte-identical behavior:
 * default range month-start → today, agent selector, `to` gets " 23:59:59"
 * appended so the backend date compare is inclusive of the whole day.
 */
import { useEffect, useMemo, useState } from 'react';
import { getApiCall } from '@/service/httpCall';
import { API_AGENTS_LIST } from '@/service/url';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import CallQualityMetricsPanel from './CallQualityMetricsPanel';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const pad = (n: any) => String(n).padStart(2, '0');
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => new Date();
const firstOfMonth = () => {
  const d = today();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

export function CallQualityReportPage() {
  const [startDate, setStartDate] = useState(toYMD(firstOfMonth()));
  const [endDate, setEndDate] = useState(toYMD(today()));
  const [agentId, setAgentId] = useState('');
  const [agentOptions, setAgentOptions] = useState<any[]>([]);

  const maxDate = toYMD(today());

  useEffect(() => {
    let mounted = true;
    getApiCall(API_AGENTS_LIST, { fields: 'min' })
      .then((resp: any) => {
        if (!mounted) return;
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        setAgentOptions(
          list
            .map((a: any) => ({
              id: a.id,
              agent_name:
                `${a.agent_name || a.name || 'Agent'}` +
                (a.agent_number ? ` (${a.agent_number})` : ''),
            }))
            .filter((a: any) => a.id != null),
        );
      })
      .catch(() => {
        if (mounted) setAgentOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const range = useMemo(
    () => ({ from: startDate, to: `${endDate} 23:59:59` }),
    [startDate, endDate],
  );

  return (
    <FillPage>
      <PageHeader
        title="Call Quality"
        description="How calls sounded — filter by date range and agent. Measured only on calls made from the browser softphone."
      />

      <div className="shadow-neu-raised mb-4 rounded-md bg-card p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="from" className="text-xs">
              From
            </Label>
            <CalendarDateInput
              id="from"
              value={startDate}
              onChange={(e) => setStartDate(e)}
              max={endDate || maxDate}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="to" className="text-xs">
              To
            </Label>
            <CalendarDateInput
              id="to"
              value={endDate}
              onChange={(e) => setEndDate(e)}
              min={startDate}
              max={maxDate}
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="agent" className="text-xs">
              Agent
            </Label>
            <Select
              value={agentId || '__all__'}
              onValueChange={(v) => setAgentId(v === '__all__' ? '' : v)}
            >
              <SelectTrigger id="agent">
                <SelectValue placeholder="All agents in my scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All agents in my scope</SelectItem>
                {agentOptions.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.agent_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <CallQualityMetricsPanel
        agentId={agentId || null}
        from={range.from}
        to={range.to}
        title={agentId ? 'Agent Call Quality' : 'Call Quality — All Agents'}
      />
    </FillPage>
  );
}

export default CallQualityReportPage;
